import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useState } from 'react'
import { Search } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { useAddItem } from '../hooks/use-item-mutations'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useProducts } from '@/features/products/hooks/use-products'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

const buildAddItemSchema = (tMenus: (key: string) => string) =>
  z
    .object({
      productId: z
        .number()
        .int()
        .positive(tMenus('validation.productRequired'))
        .optional(),
      overrideName: z.string().optional(),
      sortOrder: z.number().int(),
    })
    .refine((data) => data.productId !== undefined && data.productId > 0, {
      message: tMenus('validation.productRequired'),
      path: ['productId'],
    })

type AddItemSchema = ReturnType<typeof buildAddItemSchema>
type AddItemFormData = z.infer<AddItemSchema>

interface AddItemDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  menuId: number
  sectionId: number
}

export function AddItemDialog({
  open,
  onOpenChange,
  menuId,
  sectionId,
}: AddItemDialogProps) {
  const { t: tMenus } = useTranslation('menus')
  const [searchQuery, setSearchQuery] = useState('')
  const [popoverOpen, setPopoverOpen] = useState(false)
  const addItem = useAddItem()

  const { data: productsData } = useProducts({
    search: searchQuery || undefined,
    // A merged duplicate (CHG-2026-09-duplicate-product-merge) is not independently sellable, so
    // it must not be offered here — only its primary can be added to a menu.
    excludeMerged: true,
  })

  const form = useForm<AddItemFormData>({
    resolver: zodResolver(buildAddItemSchema(tMenus)),
    defaultValues: {
      overrideName: '',
      sortOrder: 0,
    },
  })

  const products = productsData?.items || []

  // Store selected product name separately to display even when search changes
  const [selectedProductName, setSelectedProductName] = useState<string | null>(
    null,
  )

  const onSubmit = (data: AddItemFormData) => {
    // At this point validation has passed, so productId must be defined
    if (!data.productId) {
      return
    }

    const payload = {
      productId: data.productId,
      sortOrder: data.sortOrder,
      overrideName: data.overrideName || undefined,
    }

    addItem.mutate(
      { menuId, sectionId, data: payload },
      {
        onSuccess: () => {
          form.reset()
          setSelectedProductName(null)
          setSearchQuery('')
          onOpenChange(false)
        },
      },
    )
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      form.reset()
      setSelectedProductName(null)
      setSearchQuery('')
    }
    onOpenChange(open)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{tMenus('item.addTitle')}</DialogTitle>
          <DialogDescription>{tMenus('item.addDescription')}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="productId"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{tMenus('item.product')}</FormLabel>
                  <Popover
                    open={popoverOpen}
                    onOpenChange={(nextOpen) => {
                      setPopoverOpen(nextOpen)
                    }}
                  >
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          role="combobox"
                          className="justify-between"
                        >
                          {selectedProductName || tMenus('item.selectProduct')}
                          <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-[400px] p-0">
                      <Command>
                        <CommandInput
                          placeholder={tMenus('item.searchProducts')}
                          value={searchQuery}
                          onValueChange={setSearchQuery}
                        />
                        <CommandList>
                          <CommandEmpty>
                            {tMenus('item.noProducts')}
                          </CommandEmpty>
                          <CommandGroup>
                            {products.map((product: any) => {
                              const guestName =
                                product.publicName ?? product.name
                              return (
                                <CommandItem
                                  key={product.id || product.sku}
                                  value={product.sku}
                                  onSelect={() => {
                                    field.onChange(product.id)
                                    setSelectedProductName(guestName)
                                    setPopoverOpen(false)
                                  }}
                                >
                                  <div className="flex flex-col">
                                    <span className="font-medium">
                                      {guestName}
                                    </span>
                                    <span className="truncate text-xs text-muted-foreground">
                                      {tMenus('item.sku')} {product.sku}
                                      {product.name !== guestName && (
                                        <>
                                          {' '}
                                          · {tMenus(
                                            'itemRow.internalName',
                                          )}{' '}
                                          {product.name}
                                        </>
                                      )}
                                    </span>
                                  </div>
                                </CommandItem>
                              )
                            })}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="overrideName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tMenus('item.customName')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={tMenus('item.customNamePlaceholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {tMenus('item.customNameHint')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="sortOrder"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tMenus('item.sortOrder')}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value) || 0)
                      }
                    />
                  </FormControl>
                  <FormDescription>{tMenus('item.sortHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={addItem.isPending}
              >
                {tMenus('item.cancel')}
              </Button>
              <Button type="submit" disabled={addItem.isPending}>
                {addItem.isPending ? tMenus('item.adding') : tMenus('item.add')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
